/**
 * Camera QR scanning (Req 28.3). Uses the native `BarcodeDetector` (Chrome on Android,
 * ChromeOS, macOS) when it supports `qr_code`; otherwise lazy-loads `@zxing/browser` so
 * the decoder is a separate chunk that only devices without a native detector execute
 * (it is still precached with the app shell, so scanning works offline).
 *
 * Camera access needs a secure context and the user's permission; failures are mapped to
 * {@link CameraErrorCode}s the UI explains, with typed entry always available instead.
 */

export type CameraErrorCode = 'permission_denied' | 'no_camera' | 'unsupported' | 'failed';

export class CameraError extends Error {
  constructor(readonly code: CameraErrorCode, cause?: unknown) {
    super(`camera: ${code}`);
    this.name = 'CameraError';
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/** Stops the camera and the decoder. */
export type StopScanning = () => void;

export interface CameraScanner {
  /**
   * Opens the rear camera into `video` and calls `onText` for every decoded QR (the caller
   * de-duplicates). Rejects with {@link CameraError}.
   */
  start(video: HTMLVideoElement, onText: (text: string) => void): Promise<StopScanning>;
}

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorCtor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats(): Promise<string[]>;
}

async function nativeDetector(): Promise<BarcodeDetectorLike | undefined> {
  const Ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (!Ctor) return undefined;
  try {
    const formats = await Ctor.getSupportedFormats();
    return formats.includes('qr_code') ? new Ctor({ formats: ['qr_code'] }) : undefined;
  } catch {
    return undefined;
  }
}

function cameraError(cause: unknown): CameraError {
  const name = cause instanceof Error || cause instanceof DOMException ? cause.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return new CameraError('permission_denied', cause);
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') return new CameraError('no_camera', cause);
  return new CameraError('failed', cause);
}

const SCAN_INTERVAL_MS = 200;

export function createCameraScanner(): CameraScanner {
  return {
    async start(video, onText) {
      const media = globalThis.navigator?.mediaDevices;
      if (!media?.getUserMedia) throw new CameraError('unsupported');
      let stream: MediaStream;
      try {
        stream = await media.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
      } catch (cause) {
        throw cameraError(cause);
      }
      const stopTracks = () => stream.getTracks().forEach((t) => t.stop());
      try {
        const detector = await nativeDetector();
        if (detector) {
          video.srcObject = stream;
          video.muted = true;
          video.playsInline = true;
          await video.play();
          let stopped = false;
          let timer: ReturnType<typeof setTimeout> | undefined;
          const tick = async () => {
            if (stopped) return;
            try {
              if (video.readyState >= 2) {
                for (const code of await detector.detect(video)) if (code.rawValue) onText(code.rawValue);
              }
            } catch {
              // A frame that cannot be read is skipped.
            }
            if (!stopped) timer = setTimeout(() => void tick(), SCAN_INTERVAL_MS);
          };
          void tick();
          return () => {
            stopped = true;
            if (timer !== undefined) clearTimeout(timer);
            stopTracks();
            video.srcObject = null;
          };
        }
        const { BrowserQRCodeReader } = await import('@zxing/browser');
        const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: SCAN_INTERVAL_MS });
        const controls = await reader.decodeFromStream(stream, video, (result) => {
          if (result) onText(result.getText());
        });
        return () => {
          controls.stop();
          stopTracks();
        };
      } catch (cause) {
        stopTracks();
        throw cause instanceof CameraError ? cause : cameraError(cause);
      }
    },
  };
}
